// ============================================================
// ByOurConsent.com -- Civic Actions Page
// /civic
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import api    from '../utils/api';

const US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming'
];

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

const LevelBadge = ({ level, state }) => (
    <span style={{
        fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.12em',
        textTransform: 'uppercase', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
        background: level === 'federal' ? '#e8ecf0' : '#f0e8ff',
        color: level === 'federal' ? '#2a4a8a' : '#6a2a8a'
    }}>
        {level === 'federal' ? '🏛️ Federal' : `📍 ${state || 'State'}`}
    </span>
);

const StatusBadge = ({ status }) => {
    const map = {
        active:   { label: 'Active',   bg: '#e8f5e0', color: '#3b6d11' },
        resolved: { label: 'Resolved', bg: '#e8ecf0', color: '#4a5060' },
        expired:  { label: 'Expired',  bg: '#ede8dc', color: '#8a8070' }
    };
    const s = map[status] || map.active;
    return (
        <span style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: s.bg, color: s.color }}>
            {s.label}
        </span>
    );
};

const CivicPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [actions,    setActions]    = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading,    setLoading]    = useState(true);

    const activeLevel    = searchParams.get('level')    || '';
    const activeCategory = searchParams.get('category') || '';
    const activeState    = searchParams.get('state')    || '';

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({ limit: 50, status: 'active' });
        if (activeLevel)    params.set('level', activeLevel);
        if (activeCategory) params.set('category', activeCategory);
        if (activeState)    params.set('state', activeState);

        api.get(`/civic?${params}`)
            .then(r => setActions(r.data.actions || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [activeLevel, activeCategory, activeState]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get('/articles/categories')
            .then(r => setCategories(r.data.categories || []))
            .catch(() => {});
    }, []);

    const setFilter = (key, value) => {
        const p = new URLSearchParams(searchParams);
        if (value) p.set(key, value); else p.delete(key);
        setSearchParams(p);
    };

    return (
        <>
            <Helmet>
                <title>Take Action — By Our Consent</title>
                <meta name="description" content="Civic actions you can take today to defend constitutional governance." />
            </Helmet>

            <Nav />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '2rem 1.5rem' }}>
                <div style={{ maxWidth: '860px', margin: '0 auto' }}>
                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b9a7a', marginBottom: '8px' }}>
                        Civic engagement
                    </div>
                    <h1 style={{ fontSize: '34px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.15, marginBottom: '8px' }}>
                        Take action
                    </h1>
                    <p style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#8a9aaa', maxWidth: '560px' }}>
                        Specific actions you can take today — at the federal and state level — to defend constitutional governance and hold officials accountable.
                    </p>
                </div>
            </div>

            {/* Filter bar */}
            <div style={{ background: '#ede8dc', borderBottom: '0.5px solid #c8beaa', padding: '10px 1.5rem' }}>
                <div style={{ maxWidth: '860px', margin: '0 auto' }}>

                    {/* Level row -- All/Federal/State only */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {[['','All'],['federal','Federal'],['state','State']].map(([val,label]) => (
                            <button key={val} onClick={() => setFilter('level', val)}
                                style={{ background: activeLevel===val ? '#8b1a1a' : 'transparent', color: activeLevel===val ? '#f5f0e8' : '#4a5060', border:`0.5px solid ${activeLevel===val ? '#8b1a1a' : '#c8beaa'}`, padding:'4px 14px', fontSize:'12px', fontFamily:'sans-serif', borderRadius:'20px', cursor:'pointer', fontWeight: activeLevel===val ? 600 : 400 }}>
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* State filter (only when State level selected) */}
                    {activeLevel === 'state' && (
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                            <span style={{ fontSize: '10px', fontFamily: 'sans-serif', color: '#8a8070', letterSpacing: '0.1em', textTransform: 'uppercase' }}>State</span>
                            <select
                                value={activeState}
                                onChange={e => setFilter('state', e.target.value)}
                                style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', padding: '4px 10px', fontSize: '12px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none' }}>
                                <option value="">All states</option>
                                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Category row */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: '10px', fontFamily: 'sans-serif', color: '#8a8070', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Category</span>
                        <button onClick={() => setFilter('category', '')}
                            style={{ background: !activeCategory ? '#1e2a3a' : 'transparent', color: !activeCategory ? '#e8dfc8' : '#4a5060', border:`0.5px solid ${!activeCategory ? '#1e2a3a' : '#c8beaa'}`, padding:'3px 10px', fontSize:'11px', fontFamily:'sans-serif', borderRadius:'20px', cursor:'pointer' }}>
                            All
                        </button>
                        {categories.map(c => (
                            <button key={c.slug} onClick={() => setFilter('category', c.slug)}
                                style={{ background: activeCategory===c.slug ? '#1e2a3a' : 'transparent', color: activeCategory===c.slug ? '#e8dfc8' : '#4a5060', border:`0.5px solid ${activeCategory===c.slug ? '#1e2a3a' : '#c8beaa'}`, padding:'3px 10px', fontSize:'11px', fontFamily:'sans-serif', borderRadius:'20px', cursor:'pointer' }}>
                                {c.name}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Actions list */}
            <div style={{ maxWidth: '860px', margin: '0 auto', padding: '1.5rem' }}>
                {loading ? (
                    <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>
                ) : actions.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px' }}>
                        <div style={{ fontSize: '32px', marginBottom: '10px' }}>⚡</div>
                        <div style={{ fontSize: '17px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                            No actions found
                        </div>
                        <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '6px' }}>
                            Try changing the filters above
                        </div>
                    </div>
                ) : (
                    actions.map((a, i) => (
                        <Link key={a.id} to={`/civic/${a.slug}`} style={{ textDecoration: 'none' }}>
                            <div style={{ background: i % 2 === 0 ? '#f5f0e8' : '#ede8dc', border: '0.5px solid #c8beaa', borderLeft: '3px solid #8b1a1a', borderRadius: '3px', padding: '1.25rem', marginBottom: '8px', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}
                                onMouseEnter={e => e.currentTarget.style.borderLeftColor = '#1e2a3a'}
                                onMouseLeave={e => e.currentTarget.style.borderLeftColor = '#8b1a1a'}>

                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
                                        <LevelBadge level={a.level} state={a.state} />
                                        {a.category_name && (
                                            <span style={{ fontSize: '9px', background: '#ede8dc', border: '0.5px solid #c8beaa', padding: '2px 7px', borderRadius: '20px', fontFamily: 'sans-serif', color: '#6a7080', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                {a.category_name}
                                            </span>
                                        )}
                                        <StatusBadge status={a.status} />
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '6px' }}>
                                        {a.title}
                                    </div>
                                    <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.6, marginBottom: '8px' }}>
                                        {a.description.length > 180 ? a.description.slice(0, 180) + '...' : a.description}
                                    </div>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                        {fmtDate(a.created_at)}
                                        {a.expires_at && ` · Expires ${fmtDate(a.expires_at)}`}
                                        {` · ${a.view_count} views`}
                                    </div>
                                </div>

                                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8b1a1a' }}>
                                        View details →
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))
                )}
            </div>

            <Footer />
        </>
    );
};

export default CivicPage;
